import React from 'react';
import propTypes from 'prop-types';
import { Row, Col, Button, Space } from 'antd';
import { SaveOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../common/ConfirmModal';
import { WORKFLOW_ACTION_BUTTON_COLOR, WORKFLOW_ACTION_BUTTON_ICON } from '../../constance/dataTypes';
import ColorButton from '../common/ColorButton';
import { BLUE_COLOR } from '../../constance/color';

const ActionButton = ({
  enableSave,
  enableCreate,
  actions,
  isLoading,
  handleSave,
  handleCreate,
  handleAction,
  disabled,
}) => {
  const { t } = useTranslation();
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
  const [selectedAction, setSelectedAction] = React.useState('');
  const [memo, setMemo] = React.useState('');

  const WORKFLOW_ACTION_BUTTON_ICON = {
    approve: <CheckOutlined />,
    reject: <CloseOutlined />,
  };

  return (
    <>
      <Row gutter={16}>
        <Col sm={24} md={7} lg={3}
          style={{ textAlign: 'center', marginBottom: '1.5rem', width: '100%' }}
        >
          {enableSave && (
            <ColorButton
              variant="solid"
              color='primary'
              override={BLUE_COLOR}
              icon={<SaveOutlined />}
              disabled={disabled}
              loading={isLoading}
              content={t('save')}
              onClick={handleSave}
              block
            >
              {t('save')}
            </ColorButton>
          )}
        </Col>

        <Col sm={24} md={17} lg={21}
          style={{ marginBottom: '1.5rem', width: '100%', textAlign: 'right' }}
        >
          <Space>
            {enableCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={disabled}
                loading={isLoading}
                content={t('save')}
                onClick={handleCreate}
              >
                {t('create')}
              </Button>
            )}
            {actions.map((action) => {
              const actionKey = action.split('_')[1];
              return (
                <Button
                  key={`${action}-button`}
                  color={WORKFLOW_ACTION_BUTTON_COLOR[actionKey]}
                  variant='solid'
                  icon={WORKFLOW_ACTION_BUTTON_ICON[actionKey]}
                  onClick={() => {
                    setIsConfirmVisible(true);
                    setSelectedAction(action);
                  }}
                  disabled={disabled}
                  loading={isLoading}
                >
                  {t(`workflows.actions.${action}`)}
                </Button>
              );
            })}
          </Space>
        </Col>
      </Row>

      <ConfirmModal
        open={isConfirmVisible}
        onClose={() => {
          setIsConfirmVisible(false);
          setSelectedAction('');
        }}
        title={t('workflows.confirmation_title')}
        content={`${t('workflows.confirmation_content')}: [${selectedAction ? t(`workflows.actions.${selectedAction}`) : ''
          }]?`}
        onConfirm={(memo) => handleAction({ action: selectedAction, memo })}
        showMemo
        isMemoRequired={selectedAction.includes('reject')}
      />
    </>
  );
};

export default React.memo(ActionButton);

ActionButton.defaultProps = {
  disabled: false,
  enableSave: false,
  enableCreate: false,
  actions: [],
  isLoading: false,
  handleSave: () => null,
  handleCreate: () => null,
  handleAction: () => null,
  onActionSuccess: () => null,
}

ActionButton.propTypes = {
  disabled: propTypes.bool,
  enableSave: propTypes.bool,
  enableCreate: propTypes.bool,
  actions: propTypes.array,
  isLoading: propTypes.bool,
  handleSave: propTypes.func,
  handleCreate: propTypes.func,
  handleAction: propTypes.func,
  onActionSuccess: propTypes.func,
}